import { 
    Typography,
    Card,
    CardHeader,
    CardContent, 
    IconButton, 
    Collapse, 
    Chip, 
    List, 
    ListItem, 
    ListItemText, 
    Stack, 
    Tooltip, 
    Alert,
    Box
} from "@mui/material";
import { 
    Delete, 
    ExpandMore, 
    AvTimer, 
    HourglassBottom
} from '@mui/icons-material'
import React from "react";
import { useTranslation } from "react-i18next";
import { NotificationJobStatus } from "constants/notificationJobStatus";
import ConfirmIconButton from "components/buttons/ConfirmIconButton";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
    headerActionContainer: {
        spacing: 0.5,
        alignItems: "center"
    },
    headerActionIconContainer: {
        display: 'flex',
        flexDirection: "row",
        alignItems: "center",
        gap: 0.5
    },
    orgStatusSmallChip: {
        height: 18,
        fontSize: "0.65rem",
        padding: "0 4px",
        "& .MuiChip-label": {
            padding: 0,
        },
    },
    messageDetails: {
        display: "block",
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",

        "&:hover": {
            whiteSpace: "normal",
            textOverflow: "initial",
            overflow: "visible",
            height: "auto",
        }
    }
}));

export function NotificationJobCardDetail({ job, onManualDelete, messageTitle, messageBody, nextPollIn, autoDeleteIn }) {
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const [error, setError] = React.useState();
    const { t, i18n } = useTranslation();

    const handleToggle = () => setOpen(o => !o);

    const {
        jobId,
        subtitle,
        statusLabel,
        status,
        startedAt,
        completedAt,
        processed,
        total,
        statusesByOrgId
    } = {
        jobId: job.jobId,
        subtitle: `${t('status')}: ${job.status}`,
        statusLabel: t(NotificationJobStatus[job.status].translationKey),
        status: job.status,
        startedAt: formatDate(job.startedAt, i18n.language),
        completedAt: formatDate(job.completedAt, i18n.language),
        processed: job.processed ?? '-',
        total: job.total ?? '-',
        statusesByOrgId: job.statusesByOrgId || {}
    };
    
    const nextPoll = formatCountdown(nextPollIn, () => `${t('notificationJob.nextPollAt')}: ${nextPollIn}`);
    const autoDelete = formatCountdown(autoDeleteIn, () => `${t('notificationJob.autoDeleteIn')}: ${autoDeleteIn}`);
    
    const renderOrgStatus = React.useMemo(
        () => Object.entries(statusesByOrgId).map(renderOrgStatusFn({ t, i18n, classes })),
        [classes, i18n, statusesByOrgId, t]);

    return (
        <Card 
            sx={{ opacity: 1 }}
        >
            {
                error &&
                <Alert severity="warning">
                    {t('error')}: {error}
                </Alert>
            }
            <CardHeader
                title={jobId}
                subheader={subtitle}
                sx={{ overflowWrap: 'anywhere' }}
                action={
                    <Stack className={classes.headerActionContainer}>
                        <Stack className={classes.headerActionIconContainer}>
                            <Tooltip title={open ? t('hide') : t('show')}>
                                <IconButton
                                    onClick={handleToggle}
                                    aria-expanded={open}
                                    aria-label="show details"
                                    size="small"
                                >
                                    <ExpandMore />
                                </IconButton>
                            </Tooltip>

                            <ConfirmIconButton
                                onConfirm={() => onManualDelete(jobId, setError)}
                                title={t('confirmDeletion')}
                                text={t('notificationJobs.deleteJob')}
                            >
                                <Delete />
                            </ConfirmIconButton>
                        </Stack>
                        <Chip
                            label={statusLabel}
                            color={statusColor(status)}
                            size="small"
                        />
                    </Stack>
                }
            />
            <CardContent sx={{ overflowWrap: 'anywhere' }}>
                <Stack spacing={2}>
                    <Stack spacing={0.5}>
                        <Typography variant="body2" className={classes.messageDetails}><b>{t('title')}:</b> {messageTitle}</Typography>
                        <Typography variant="body2" className={classes.messageDetails}><b>{t('body')}:</b> {messageBody}</Typography>
                    </Stack>
                    <Stack spacing={1}>
                        <Typography variant="body2">{t('startedAt')}: {startedAt}</Typography>
                        <Typography variant="body2">{t('completedAt')}: {completedAt}</Typography>
                        <Typography variant="body2">{`${t('initiatives')} ${t('processed.female')}`}: {processed} / {total}</Typography>
                    </Stack>
                </Stack>
                <Stack spacing={0.3} sx={{ mt: 1 }}>
                    {
                        nextPoll &&
                        <TimerCountdown
                            text={nextPoll}
                            icon={AvTimer}
                            color={"secondary.main"}
                        />
                    }
                    {
                        autoDelete &&
                        <TimerCountdown
                            text={autoDelete}
                            icon={HourglassBottom}
                            color={"error.main"}
                        />
                    }
                </Stack>
            </CardContent>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <CardContent>
                    <Typography variant="subtitle2"><b>{t('notificationJob.orgStatuses')}</b></Typography>
                    <List>
                        {renderOrgStatus}
                    </List>
                </CardContent>
            </Collapse>
        </Card>
    );
}

function formatDate(iso, language) {
    if (!iso || !language) return '-';
    return new Date(iso).toLocaleString(language);
};

function formatCountdown(secondsRemaining, formatterFn) {
    if(typeof secondsRemaining !== 'number' || secondsRemaining < 0) return null;
    return formatterFn();
}

function statusColor(status) {
    return NotificationJobStatus[status?.toUpperCase()]?.statusColor || 'default';
}

function renderOrgStatusFn({ t, i18n, classes }) {
    return function ([orgId, s]) {
        const {
            organizationTitle,
            status,
            statusLabel,
            startedAt,
            completedAt,
            errorMessage
        } = {
            organizationTitle: `${s.organizationTitle || s.orgId || orgId} — `,
            status: s.status,
            statusLabel: t(NotificationJobStatus[s.status].translationKey),
            startedAt: formatDate(s.startedAt, i18n.language),
            completedAt: formatDate(s.completedAt, i18n.language),
            errorMessage: s.errorMessage
        };

        return (
            <ListItem
                key={orgId}
                alignItems="flex-start"
            >
                <ListItemText
                    primary={
                        <Typography>
                            {organizationTitle}
                            <Chip
                                label={statusLabel}
                                color={statusColor(status)}
                                className={classes.orgStatusSmallChip}
                            />
                        </Typography>
                    }
                    secondary={
                        <>
                            <div>{t('startedAt')}: {startedAt}</div>
                            <div>{t('completedAt')}: {completedAt}</div>
                            {
                                errorMessage
                                    ? <div style={{ color: 'red' }}>{errorMessage}</div>
                                    : null
                            }
                        </>
                    }
                />
            </ListItem>
        );
    };
}

function TimerCountdown({icon: Icon, text, color}) {
    return (
        <Box alignItems="center" display="flex">
            <Icon fontSize="small" size="small" sx={{color: color}}/>
            <Typography variant="caption" color={color}>
                {text}
            </Typography>
        </Box>
    )
}
